import { Typography } from "@mui/material";
import InfoIcon from "components/SVGs/Info";
import { useTranslation } from "react-i18next";
import Button from "components/Button";
import Loader from "components/Loader";
import useToast from "hooks/useToast";
import DropZone, {
  columnsAr,
  columnsEn,
  IClassRecordWithError,
} from "./DropZone";
import DownloadSample, { sheetColumns } from "./DownloadSample";
import { useContext, useEffect, useState } from "react";
import { validate } from "../utils/validate";
import { IOption, IValidationErrors } from "features/common/Interfaces";
import { IFile, IUploadedRow } from "../../Content/useFileBuilder";
import { IAddClassRequest, useAddClassMutation } from "app/services/classes";
import { MasterDataContext } from "contexts/MasterDataContext";
import { gradesList, teachersList } from "services/Common";
import { FireBaseContext } from "contexts/FireBase";
type GradeCode =
  | "Kindergarten 1"
  | "Kindergarten 2"
  | "Grade 1"
  | "Grade 2"
  | "Grade 3"
  | "Grade 4";
const gradesAr = {
  "Kindergarten 1": "الروضة الأولى",
  "Kindergarten 2": "الروضة الثانية",
  "Grade 1": "الصف الأول",
  "Grade 2": "الصف الثاني",
  "Grade 3": "الصف الثالث",
  "Grade 4": "الصف الرابع",
};
const gradesEn = {
  "Kindergarten 1": "Kindergarten 1",
  "Kindergarten 2": "Kindergarten 2",
  "Grade 1": "Grade 1",
  "Grade 2": "Grade 2",
  "Grade 3": "Grade 3",
  "Grade 4": "Grade 4",
};
const customStyles = {
  root: {
    direction: "rtl",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "580px",
    padding: "20px",
    backgroundColor: "rgb(255, 255, 255)",
    borderRadius: "17px",
    overflowY: "auto",
    overflowX: "hidden",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  note: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    paddingBottom: "20px",
  },
};

interface IProps {
  onSubmit(): void;
}

const UploadFile = ({ onSubmit }: IProps) => {
  const { logEvent } = useContext(FireBaseContext);
  const [validData, setValidData] = useState<IClassRecordWithError[]>();
  const [errorData, setErrorData] = useState<IClassRecordWithError[]>();
  const {
    state: { schoolDetails },
  } = useContext(MasterDataContext);
  const [grades, setGrades] = useState<IOption[]>([]);
  const [teachers, setTeachers] = useState<IOption[]>([]);
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const [addClass, { isLoading }] = useAddClassMutation();

  const fetchGrades = async () => {
    try {
      const response = await gradesList();
      if (response?._statusCode === 200) {
        const list = response._entity?.map((item: any) => ({
          value: item._id,
          label: item._code,
        }));
        setGrades(list);
      } else throw response;
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTeachers = async (schoolId: number) => {
    try {
      const response = await teachersList(schoolId);
      if (response?._statusCode === 200) {
        const list = response._entity?.map((item: any) => ({
          value: item._teacherId,
          label: `${item?._name ?? ""} ${item?._lastName ?? ""}`,
        }));
        setTeachers(list);
      } else throw response;
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchGrades();
    if (schoolDetails?.schoolId) fetchTeachers(schoolDetails?.schoolId);
  }, [schoolDetails?.schoolId]);

  const createHandler = async () => {
    if (validData?.length)
      try {
        const recordsInfo: IClassRecordWithError[] = structuredClone(validData);
        const classesInfo: IAddClassRequest[] = recordsInfo.map((rec) => ({
          _name: rec.className,
          _gradeId: rec.gradeId!,
          _teacherId: rec.teacherId!,
        }));
        const data = await addClass(classesInfo).unwrap();
        if (data?._statusCode === 200) {
          if (!errorData?.length) {
            toast({
              label: t("success"),
              message: t("Classes Added"),
              rtl: true,
              type: "success",
              toastId: "classesAdded",
            });
            onSubmit();
          } else {
            toast({
              label: t("Note"),
              message: t("Some Classes not Added"),
              rtl: i18n.dir() === "rtl",
              type: "success",
              toastId: "studentsAdd",
            });
            setValidData(undefined);
          }
        } else throw data;
      } catch (error) {
        toast({
          label: t("error"),
          message: t("Something went wrong"),
          rtl: i18n.dir() === "rtl",
          type: "error",
          toastId: "studentsAdd",
        });
        console.log(error);
      }
    logEvent("ClickOnCreateClass", { Creation_Type: "Excel bulk" });
  };

  const validateUploadedData = (file: IFile, language: "ar" | "en") => {
    if (file?.data?.length === 0) {
      setValidData(undefined);
      setErrorData(undefined);
      return;
    }
    const updateValidData: IClassRecordWithError[] = [];
    const invalidData: IClassRecordWithError[] = [];
    const uploadedFile = structuredClone(file);
    const columns = language === "ar" ? columnsAr : columnsEn;
    uploadedFile.data.forEach((row) => {
      let {
        [columns[0]]: gradeName,
        [columns[1]]: teacherName,
        [columns[2]]: className,
      } = row as IUploadedRow;
      if (!(className as string)?.trim()?.length && !gradeName && !teacherName)
        return;

      if (className || gradeName || teacherName) {
        const gradeId =
          (grades.find((grade) =>
            [
              gradesAr[grade.label as GradeCode],
              gradesEn[grade.label as GradeCode].toLowerCase(),
            ].includes(
              (gradeName?.toString().trim() as GradeCode)?.toLowerCase()
            )
          )?.value as number) ?? gradeName;
        const teacherId =
          (teachers.find(
            (teacher) => teacher.label.trim() === teacherName?.toString().trim()
          )?.value as number) ?? teacherName;
        const rowData: IClassRecordWithError = {
          gradeId,
          teacherId,
          className: className?.toString()?.trim(),
        };

        const errors = validate(rowData, {});
        if (!className && (teacherName || gradeName))
          errors["className"] = "Class Name is required";
        if (gradeName || className) {
          if (!teacherName) errors["teacherId"] = "Teacher is required";
          else if (teacherName === teacherId)
            errors["teacherId"] = "Incorrect Teacher name";
        }
        if (teacherName || className)
          if (!gradeName) errors["gradeId"] = "Grade is required";
          else if (gradeName === gradeId)
            errors["gradeId"] = "Incorrect Grade name";

        if (Object.keys(errors).length !== 0) {
          const translateErrors: IValidationErrors = {};
          const sheetColumnsNames = {
            gradeId: sheetColumns[0],
            teacherId: sheetColumns[1],
            className: sheetColumns[2],
          };
          for (const error in errors) {
            translateErrors[
              t(
                sheetColumnsNames[
                  error as "gradeId" | "teacherId" | "className"
                ]
              )
            ] = t(errors[error]);
          }
          rowData.errors = JSON.stringify(translateErrors);
          invalidData.push(rowData);
        } else {
          updateValidData.push(rowData);
        }
      }
    });

    if (updateValidData.length === 0 && invalidData?.length) {
      toast({
        label: t("error"),
        message: t("Adding classes failed"),
        rtl: i18n.dir() === "rtl",
        type: "error",
        toastId: "Uploads-has-failed",
      });
    }
    setValidData(updateValidData);
    setErrorData(invalidData);
  };
  return (
    <>
      <Loader open={isLoading} />
      <Typography
        component="div"
        sx={customStyles.root}
        tabIndex={-1}
        style={{ direction: i18n.dir() }}
      >
        <Typography component="div" sx={customStyles.note}>
          <InfoIcon />
          <Typography sx={{ color: "rgb(105 123 140)", fontSize: "16px" }}>
            {t("Download File to Upload classes")}
          </Typography>
        </Typography>
        <DownloadSample />
        <DropZone
          errorData={errorData}
          showError={!validData?.length}
          grades={grades}
          teachers={teachers}
          validateUploadedData={validateUploadedData}
        />
        <Typography
          component="div"
          sx={{ display: "flex", flexDirection: "row", width: "100%" }}
        >
          <Button
            label={t("Create classes")}
            action={createHandler}
            styles={{ width: "125px", fontSize: "14px" }}
            disable={!validData?.length}
          />
        </Typography>
      </Typography>
    </>
  );
};

export default UploadFile;
